import React, { useState, useCallback, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { ConnectRefreshActiveStatusBanner } from '../ConnectAnnouncementBanner/ConnectRefreshActiveStatusBanner';
import { ConnectMatchesNotificationBanner } from '../ConnectAnnouncementBanner/ConnectMatchesNotificationBanner';
import { ConnectMatchQuizBanner } from '../ConnectAnnouncementBanner/ConnectMatchQuizBanner';
import {
  useShouldDisplayRefreshActiveJobSeekingStatusBanner,
  useShouldDisplayMatchesNotificationBanner,
  useShouldDisplayMatchQuizBanner,
  resetDismissedBanners,
} from './utils';
import { connectRedirectUrls, candidatePortalRedirectUrls } from 'utils/constants/urls';
import { SESSION_STORAGE_KEYS } from 'utils/constants';
import { Box, styled, Fade } from '@mui/material';
import { useBannerContext } from '../../features/Context/BannerContextProvider';
import { BannerConfig } from './types';
import { NAVBAR_HEIGHT_DESKTOP, NAVBAR_HEIGHT_MOBILE } from './constants';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { useLocation } from 'react-router-dom';
import { BannerKeys } from 'utils/constants/banners';

export function BannerManager(): React.ReactElement | null {
  const location = useLocation();
  const { setBannerHeight, setCurrentBannerKey } = useBannerContext();
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([]);
  const { candidateMatches, matchQuizStatus, isFetchingMatchQuizStatus, isFetchingMatches } =
    useConnectContext();
  const handleBannerDismiss = useCallback((key: string) => {
    setDismissedBanners((prev) => {
      const updated = [...prev, key];
      sessionStorage.setItem(SESSION_STORAGE_KEYS.DISMISSED_BANNERS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Call custom hooks at the top level
  const shouldDisplayRefreshActiveJobSeekingStatusBanner =
    useShouldDisplayRefreshActiveJobSeekingStatusBanner();
  const shouldDisplayMatchesNotificationBanner = useShouldDisplayMatchesNotificationBanner();
  const shouldDisplayMatchQuizBanner = useShouldDisplayMatchQuizBanner();

  // Combine all relevant loading states
  const isBannerEligibilityLoading = isFetchingMatchQuizStatus || isFetchingMatches;
  // Add any other loading flags from your custom hooks if available

  // bannerOptions now uses the booleans from the hooks
  const bannerOptions: BannerConfig[] = useMemo(
    () => [
      {
        key: BannerKeys.REFRESH_ACTIVE_STATUS,
        component: (props) => <ConnectRefreshActiveStatusBanner {...props} />,
        routesToDisplay: [
          connectRedirectUrls.MISSOURI_JOB_DISCOVERY,
          connectRedirectUrls.TENNESSEE_JOB_DISCOVERY,
          candidatePortalRedirectUrls.CANDIDATE_PORTAL,
        ],
        shouldDisplay: () => shouldDisplayRefreshActiveJobSeekingStatusBanner,
      },
      {
        key: BannerKeys.MATCHES_NOTIFICATION,
        component: (props) => <ConnectMatchesNotificationBanner {...props} />,
        routesToDisplay: [
          connectRedirectUrls.MISSOURI_JOB_DISCOVERY,
          connectRedirectUrls.TENNESSEE_JOB_DISCOVERY,
        ],
        shouldDisplay: () => shouldDisplayMatchesNotificationBanner,
      },
      {
        key: BannerKeys.MATCH_QUIZ,
        component: (props) => <ConnectMatchQuizBanner {...props} />,
        routesToDisplay: [
          connectRedirectUrls.MISSOURI_JOB_DISCOVERY,
          connectRedirectUrls.TENNESSEE_JOB_DISCOVERY,
          candidatePortalRedirectUrls.CANDIDATE_PORTAL,
        ],
        shouldDisplay: () => shouldDisplayMatchQuizBanner,
      },
    ],
    [
      shouldDisplayRefreshActiveJobSeekingStatusBanner,
      shouldDisplayMatchesNotificationBanner,
      shouldDisplayMatchQuizBanner,
    ]
  );

  const getEligibleBanner = useCallback(
    () =>
      bannerOptions?.find(
        (banner) =>
          !dismissedBanners?.includes(banner?.key) &&
          (!banner?.routesToDisplay ||
            banner?.routesToDisplay?.some((route) => window.location.pathname.startsWith(route))) &&
          (!banner?.shouldDisplay || banner?.shouldDisplay())
      ),
    [dismissedBanners, bannerOptions]
  );

  const [showCurrent, setShowCurrent] = useState(true);
  const bannerRef = useRef<HTMLDivElement>(null);
  const prevBannerHeight = useRef(0);

  // Always compute eligibleBanner directly
  const eligibleBanner = getEligibleBanner();

  useEffect(() => {
    resetDismissedBanners();
  }, [location.pathname]);

  // Sync currentBannerKey in context
  useEffect(() => {
    setCurrentBannerKey(eligibleBanner?.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleBanner]);

  // Sync bannerHeight
  useLayoutEffect(() => {
    if (!eligibleBanner) {
      return setBannerHeight(0);
    }

    if (!bannerRef?.current) return () => {};

    const updateBannerHeight = () => {
      if (bannerRef?.current) {
        const height = bannerRef?.current?.offsetHeight || 64;
        setBannerHeight(height);
        prevBannerHeight.current = height;
      } else {
        setBannerHeight(64);
      }
    };

    updateBannerHeight();
    // Re-check after fade transition (300ms)
    const timeout = setTimeout(updateBannerHeight, 350);

    const resizeObserver = new window.ResizeObserver(updateBannerHeight);
    if (bannerRef?.current) {
      resizeObserver.observe(bannerRef?.current);
    }

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleBanner, showCurrent, candidateMatches, matchQuizStatus]);

  const handleEntered = () => {
    if (bannerRef?.current) {
      setBannerHeight(bannerRef?.current?.offsetHeight);
    }
  };

  const handleExited = () => {
    setShowCurrent(true);
  };

  return (
    <FixedBannerWrapper>
      {!isBannerEligibilityLoading && (
        <Fade
          in={showCurrent}
          timeout={300}
          onEntered={handleEntered}
          onExited={handleExited}
          appear
          mountOnEnter
          unmountOnExit
        >
          <Box ref={bannerRef}>
            {!!eligibleBanner && (
              <eligibleBanner.component onDismiss={() => handleBannerDismiss(eligibleBanner.key)} />
            )}
          </Box>
        </Fade>
      )}
    </FixedBannerWrapper>
  );
}

const FixedBannerWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: `${NAVBAR_HEIGHT_DESKTOP}px`,
  left: 0,
  width: '100%',
  zIndex: 1201,
  margin: 0,
  [theme.breakpoints.down('lg')]: {
    top: `${NAVBAR_HEIGHT_MOBILE}px`,
  },
}));
